import React, { useEffect, useState } from 'react';
import { SelectValue } from 'antd/lib/select';

import IbButton from '../../../../components/common/IbButton';
import IbModal, { IbModalPosition } from '../../../../components/common/IbModal';
import IbTypography from '../../../../components/common/IbTypography';
import IbInput from '../../../../components/common/IbInput';
import { CheckFieldValueFnMap, useValidationForm } from '../../../../utils/validationUtil';
import { ValidationErrors } from '../../../../types';
import {
  DoclingOCRProviderEngine,
  DoclingOCRProviderSettings,
  VLMOCRProviderSettings,
  LLMSettings,
  OCRProviderCreationRequest,
  OCRProviderModel,
  OCRProviderSettingsType,
  OCRProviderUpdatingRequest,
  ProviderModel,
  Settings6,
} from '../../../../../gamma-api';
import { getTranslateFunc } from '../../../../utils/stringUtil';
import IbSelect from '../../../../components/common/IbSelect';
import ModelSelector from '../../../../components/ModelSelector';
import PromptEditorExpandable from '../../../../components/common/PromptEditorExpandable';
import { PromptLanguage } from '../../../../components/common/PromptEditor/const';
import { CodeEditorSize } from '../../../../components/common/CodeEditorExpandable/types';
import JsonEditorExpandable from '../../../../components/common/JsonEditorExpandable';
import { CodeEditorStatus } from '../../../../components/common/CodeEditor/types';

import './index.less';

import { CONTENT_CLASS_NAME, DEFAULT_OCR_PROVIDER, FORM_ITEM_CLASS_NAME, MAIN_CLASS_NAME } from './const';
import { getDoclingReferenceDocUrl, getDoclingServerDocsUrl, getEmptyOcrSettings, getOcrExtraParams } from './utils';

type OcrProviderForm = {
  name: string;
  type: string;
  model_name: string;
  base_url: string;
  ocr_engine: string;
  extra_params: string;
};

const checkFieldValueFnMap: CheckFieldValueFnMap<OcrProviderForm> = {
  name: (value: string) => !!value,
  type: (value: string) => !!value,
  model_name: (value: string, form: OcrProviderForm) => form.type !== OCRProviderSettingsType.Vlm || !!value,
  base_url: (value: string, form: OcrProviderForm) => form.type !== OCRProviderSettingsType.Docling || !!value,
  ocr_engine: (value: string, form: OcrProviderForm) => form.type !== OCRProviderSettingsType.Docling || !!value,
  extra_params: (value: string, form: OcrProviderForm) => form.type !== OCRProviderSettingsType.Docling || !!value,
};

interface IOcrProviderModalProps {
  visible: boolean;
  llmProviders: ProviderModel[];
  llmProvidersLoading: boolean;
  editOcrProvider?: OCRProviderModel;
  onCreate: (
    ocrProvider: OCRProviderCreationRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ) => Promise<void>;
  onUpdate: (
    ocrProvider: OCRProviderUpdatingRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ) => Promise<void>;
  onCancel: () => void;
}

const OcrProviderModal: React.FC<IOcrProviderModalProps> = ({
  visible,
  llmProviders,
  llmProvidersLoading,
  editOcrProvider,
  onCreate,
  onUpdate,
  onCancel,
}) => {
  const formatExtraParams = (newOcrProvider: OCRProviderModel): string => {
    if (newOcrProvider.settings.type !== OCRProviderSettingsType.Docling) {
      return '';
    }

    return JSON.stringify((newOcrProvider.settings as DoclingOCRProviderSettings).extra_params, undefined, 2);
  };

  const [saving, setSaving] = useState(false);

  const [ocrProvider, setOcrProvider] = useState<OCRProviderModel>(DEFAULT_OCR_PROVIDER);
  const [extraParams, setExtraParams] = useState(formatExtraParams(DEFAULT_OCR_PROVIDER));

  const translate = getTranslateFunc('OCR: Provider: Modal:');

  const initialFormValues: OcrProviderForm = {
    name: editOcrProvider?.name ?? '',
    type: editOcrProvider?.settings.type ?? DEFAULT_OCR_PROVIDER.settings.type,
    model_name: (editOcrProvider?.settings as VLMOCRProviderSettings)?.vlm_settings?.model_name ?? '',
    base_url: (editOcrProvider?.settings as DoclingOCRProviderSettings)?.base_url ?? '',
    ocr_engine: (editOcrProvider?.settings as DoclingOCRProviderSettings)?.ocr_engine ?? DoclingOCRProviderEngine.Auto,
    extra_params: formatExtraParams(editOcrProvider ?? DEFAULT_OCR_PROVIDER),
  };

  const {
    validationState,
    onFieldChange,
    onFieldBlur,
    onSubmitErrors,
    validate,
    reset,
    getShowError,
    getErrorMessage,
  } = useValidationForm<OcrProviderForm>(initialFormValues, checkFieldValueFnMap);

  const updateExtraParams = () => {
    const valueText = formatExtraParams(ocrProvider);
    setExtraParams(valueText);
    onFieldChange('extra_params', valueText, false);
  };

  useEffect(updateExtraParams, [
    ocrProvider.settings.type,
    (ocrProvider.settings as DoclingOCRProviderSettings).ocr_engine,
  ]);

  const describeExtraParams = () => {
    const parts = [translate('Extra params: Description: Main'), translate('Extra params: Description: Lang')];
    const settings = ocrProvider.settings as DoclingOCRProviderSettings;
    const engine = settings.ocr_engine;

    if (engine != DoclingOCRProviderEngine.None && engine != DoclingOCRProviderEngine.Auto) {
      parts.push(translate(`Extra params: Description: ${engine}`));
    }

    return parts.join(' ');
  };

  const onVisiblePropChange = () => {
    if (!visible) return;

    const newOcrProvider = editOcrProvider ?? DEFAULT_OCR_PROVIDER;

    setSaving(false);
    setOcrProvider(newOcrProvider);
    setExtraParams(formatExtraParams(newOcrProvider));
    reset();
  };
  useEffect(onVisiblePropChange, [visible]);

  const onNameChange = (value: string) => {
    setOcrProvider({ ...ocrProvider, name: value });
    onFieldChange('name', value);
  };

  const onNameBlur = () => onFieldBlur('name');

  const onTypeChange = (value: SelectValue) => {
    const type = value as OCRProviderSettingsType;
    const newOcrProvider = {
      ...ocrProvider,
      settings: { ...getEmptyOcrSettings(type), ...ocrProvider.settings, type } as Settings6,
    };
    setOcrProvider(newOcrProvider);
    onFieldChange('type', type);
  };

  const onTypeBlur = () => onFieldBlur('type');

  const onVlmSettingsChange = (value: LLMSettings) => {
    setOcrProvider({ ...ocrProvider, settings: { ...ocrProvider.settings, vlm_settings: value } as Settings6 });
    onFieldChange('model_name', value.model_name ?? '');
  };

  const onVlmSettingsBlur = () => onFieldBlur('model_name');

  const onDefaultPromptChange = (value: string) => {
    setOcrProvider({ ...ocrProvider, settings: { ...ocrProvider.settings, default_prompt: value } as Settings6 });
  };

  const onBaseUrlChange = (value: string) => {
    setOcrProvider({ ...ocrProvider, settings: { ...ocrProvider.settings, base_url: value } as Settings6 });
    onFieldChange('base_url', value);
  };

  const onBaseUrlBlur = () => onFieldBlur('base_url');

  const onOcrEngineChange = (value: SelectValue) => {
    const engine = value as DoclingOCRProviderEngine;
    let newOcrProvider: OCRProviderModel;

    if (editOcrProvider?.settings.type === OCRProviderSettingsType.Docling || validationState.extra_params.isTouched) {
      newOcrProvider = { ...ocrProvider, settings: { ...ocrProvider.settings, ocr_engine: engine } as Settings6 };
    } else {
      newOcrProvider = {
        ...ocrProvider,
        settings: {
          ...ocrProvider.settings,
          ocr_engine: engine,
          extra_params: getOcrExtraParams(engine),
        } as Settings6,
      };
    }

    setOcrProvider(newOcrProvider);
    onFieldChange('ocr_engine', engine);
  };

  const onOcrEngineBlur = () => onFieldBlur('ocr_engine');

  const onExtraParamsChange = (value?: string) => {
    let valueData: { [key: string]: unknown } | null;
    let valueText: string;
    let formValue: string;

    if (value) {
      try {
        const parsedValue = JSON.parse(value);
        if (typeof parsedValue === 'object') {
          valueData = parsedValue;
          valueText = value;
          formValue = value;
        } else {
          valueData = null;
          valueText = value;
          formValue = '';
        }
      } catch {
        valueData = null;
        valueText = value;
        formValue = '';
      }
    } else {
      valueData = {};
      valueText = '';
      formValue = valueText;
    }

    if (valueData) {
      setOcrProvider({
        ...ocrProvider,
        settings: { ...ocrProvider.settings, extra_params: valueData } as Settings6,
      });
    }

    setExtraParams(valueText);
    onFieldChange('extra_params', formValue);
  };

  const onCreateButtonClick = async () => {
    if (!validate()) return;

    setSaving(true);
    await onCreate(ocrProvider, onSubmitErrors);
    setSaving(false);
  };

  const onUpdateButtonClick = async () => {
    if (!editOcrProvider || !validate()) return;

    setSaving(true);
    await onUpdate(ocrProvider, onSubmitErrors);
    setSaving(false);
  };

  const renderVlmSettings = () => {
    const settings = ocrProvider.settings as VLMOCRProviderSettings;

    return (
      <>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('VLM settings: Title')}
          </IbTypography.Paragraph>
          <IbTypography.Paragraph type="secondary">{translate('VLM settings: Description')}</IbTypography.Paragraph>
          <ModelSelector.LLM
            loading={llmProvidersLoading}
            providers={llmProviders}
            settings={settings.vlm_settings}
            onBlur={onVlmSettingsBlur}
            onChange={onVlmSettingsChange}
          />
          {getShowError(validationState.model_name) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.model_name, translate('VLM settings: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('Default prompt: Title')}
          </IbTypography.Paragraph>
          <IbTypography.Paragraph type="secondary">{translate('Default prompt: Description')}</IbTypography.Paragraph>
          <PromptEditorExpandable
            promptLanguage={PromptLanguage.PLAIN_TEXT}
            size={CodeEditorSize.MD}
            value={settings.default_prompt}
            onChange={onDefaultPromptChange}
          />
        </div>
      </>
    );
  };

  const renderDoclingSettings = () => {
    const settings = ocrProvider.settings as DoclingOCRProviderSettings;

    const options = [
      { value: DoclingOCRProviderEngine.None, label: translate('OCR engine: Value: None') },
      { value: DoclingOCRProviderEngine.Auto, label: translate('OCR engine: Value: Auto') },
      { value: DoclingOCRProviderEngine.EasyOcr, label: translate('OCR engine: Value: EasyOCR') },
      { value: DoclingOCRProviderEngine.RapidOcr, label: translate('OCR engine: Value: RapidOCR') },
      { value: DoclingOCRProviderEngine.Tesseract, label: translate('OCR engine: Value: Tesseract') },
    ];

    return (
      <>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('Base URL: Title')}
          </IbTypography.Paragraph>
          <IbInput
            status={getShowError(validationState.base_url) ? 'error' : 'default'}
            value={settings.base_url}
            onBlur={onBaseUrlBlur}
            onChange={onBaseUrlChange}
          />
          {getShowError(validationState.base_url) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.base_url, translate('Base URL: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('OCR engine: Title')}
          </IbTypography.Paragraph>
          <IbSelect
            options={options}
            status={getShowError(validationState.ocr_engine) ? 'error' : 'default'}
            value={settings.ocr_engine}
            onBlur={onOcrEngineBlur}
            onChange={onOcrEngineChange}
          />
          {getShowError(validationState.ocr_engine) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.ocr_engine, translate('OCR engine: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('Extra params: Title')}
          </IbTypography.Paragraph>
          <IbTypography.Paragraph type="secondary">{describeExtraParams()}</IbTypography.Paragraph>
          <IbTypography.Paragraph strong type="secondary">
            <a href={getDoclingServerDocsUrl(settings.base_url)} rel="noreferrer" target="_blank">
              {translate('Extra params: Link: Doc')}
            </a>
          </IbTypography.Paragraph>
          <IbTypography.Paragraph strong type="secondary">
            <a href={getDoclingReferenceDocUrl()} rel="noreferrer" target="_blank">
              {translate('Extra params: Link: Ref')}
            </a>
          </IbTypography.Paragraph>
          <JsonEditorExpandable
            schema={{}}
            size={CodeEditorSize.MD}
            status={getShowError(validationState.extra_params) ? CodeEditorStatus.Error : CodeEditorStatus.Default}
            value={extraParams}
            onChange={onExtraParamsChange}
          />
          {getShowError(validationState.extra_params) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.extra_params, translate('Extra params: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
      </>
    );
  };

  const renderSettings = () => {
    if (ocrProvider.settings.type === OCRProviderSettingsType.Vlm) {
      return renderVlmSettings();
    }

    if (ocrProvider.settings.type === OCRProviderSettingsType.Docling) {
      return renderDoclingSettings();
    }

    return null;
  };

  const renderContent = () => {
    const options = [
      { value: OCRProviderSettingsType.Vlm, label: translate('Type: Value: VLM') },
      { value: OCRProviderSettingsType.Docling, label: translate('Type: Value: Docling') },
    ];

    return (
      <div className={CONTENT_CLASS_NAME}>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('Name: Title')}
          </IbTypography.Paragraph>
          <IbInput
            status={getShowError(validationState.name) ? 'error' : 'default'}
            value={ocrProvider.name}
            onBlur={onNameBlur}
            onChange={onNameChange}
          />
          {getShowError(validationState.name) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.name, translate('Name: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('Type: Title')}
          </IbTypography.Paragraph>
          <IbSelect
            options={options}
            status={getShowError(validationState.type) ? 'error' : 'default'}
            value={ocrProvider.settings.type}
            onBlur={onTypeBlur}
            onChange={onTypeChange}
          />
          {getShowError(validationState.type) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.type, translate('Type: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        {renderSettings()}
      </div>
    );
  };

  const renderFooter = () => {
    return (
      <>
        {editOcrProvider ? (
          <IbButton type="primary" onClick={onUpdateButtonClick}>
            {translate('Button: Update')}
          </IbButton>
        ) : (
          <IbButton type="primary" onClick={onCreateButtonClick}>
            {translate('Button: Create')}
          </IbButton>
        )}
        <IbButton type="secondary" onClick={onCancel}>
          {translate('Button: Cancel')}
        </IbButton>
      </>
    );
  };

  const title = editOcrProvider ? translate('Title: Update') : translate('Title: Create');

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderFooter()}
      loading={saving}
      position={IbModalPosition.Centered}
      title={title}
      visible={visible}
      onCancel={onCancel}
    >
      {renderContent()}
    </IbModal>
  );
};

export default OcrProviderModal;
